/**
 * Typed entry points for drawing usage analytics.
 *
 * Every tracked interaction goes through one of the four `track*` helpers below
 * rather than calling the event queue directly, so the string keys that end up
 * in `EventLog.context` — and that the back-office groups on — live in exactly
 * one place. The back-office mirrors these unions in its own label Records.
 *
 * The event *family* is carried by `EventType`; the specific tool, action,
 * feature or assist is carried by `context`. See the note in
 * `event-logs.types.ts` for why it is modelled that way.
 */

import { EventType } from "@/types/event-logs.types";
import type { EventContext } from "@/types/event-logs.types";
// Imported from the leaf module rather than the `@/drawing` barrel: BlockDragger
// imports this file, and going through the barrel would close a cycle.
import type { DraggerType } from "@/drawing/draggers/types";
import { enqueueEvent } from "./event-queue";

/** Where a tool was invoked from. */
export type ToolSource =
  | "toolbar"
  | "blocks"
  | "zoom"
  | "menu"
  | "keyboard"
  /** Performed directly on the drawing, with no menu involved. */
  | "canvas";

/**
 * Toolbar commands, tracked in the `tools` family alongside the draggers.
 *
 * They are not draggers — nothing stays armed afterwards — but from the user's
 * point of view they are part of using the drawing tools, and that is the view
 * the charts have to reflect. `block_inserted` sits here next to the `block`
 * dragger on purpose: the gap between the two is how many blocks get armed and
 * never placed.
 */
export type ToolCommand = "undo" | "redo" | "block_inserted";

/** Document-level operations, outside the drawing tools themselves. */
export type DrawingAction = "manual_save" | "export_dwg";

/** Whether an action was triggered from a menu or a keyboard shortcut. */
export type ActionSource = "menu" | "keyboard";

/**
 * Residual canvas features that belong to no drawer tab and no selection bar:
 * header fullscreen, the ruler on the canvas itself, and the legend popover of
 * the tool defaults bar. Everything tab-owned lives in its tab family (cf.
 * TabActions), selection-bar actions in trackSelection.
 */
export type DrawingFeature = "fullscreen" | "legend_catalog_filtered" | "ruler_moved";

export type DrawingAssist =
  | "ortho"
  | "polar"
  | "polar_angle"
  | "object_snap"
  | "close_contour"
  | "two_point_mode"
  | "label"
  | "additive_selection"
  | "eraser_size";

/**
 * True while the drawing is restoring persisted settings.
 *
 * `DraggerMenuState.onDrawingOpen()` replays the user's stored preferences
 * through the very setters this module instruments. Without this guard, simply
 * opening a drawing would look like the user toggling half a dozen settings,
 * and every chart would be dominated by activity nobody performed.
 */
let isRestoring = false;

/** Run `restore` with all tracking suppressed. */
export function withRestoreSuppressed<T>(restore: () => T): T {
  isRestoring = true;
  try {
    return restore();
  } finally {
    isRestoring = false;
  }
}

/**
 * A tool was used — either a dragger being armed, or one of the toolbar
 * commands in `ToolCommand`.
 *
 * The parameter is a union of literals rather than `string` so a mistyped tool
 * name fails to compile. That matters: the server deliberately does not
 * validate the vocabulary, so a typo would otherwise sail through and show up
 * in the back-office as a plausible-looking extra tool.
 */
export function trackTool(
  tool: DraggerType | ToolCommand,
  source: ToolSource = "toolbar",
  details?: EventContext,
): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_TOOL_USED, { tool, source, ...details });
}

export function trackAction(
  action: DrawingAction,
  source?: ActionSource,
): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_ACTION, {
    action,
    ...(source ? { source } : {}),
  });
}

export function trackFeature(
  feature: DrawingFeature,
  details?: EventContext,
): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_FEATURE_USED, { feature, ...details });
}

/**
 * A drawing aid was changed. `value` is the new setting — a boolean for the
 * toggles, the angle for polar mode, the aperture for the eraser. Charts group
 * on `assist` alone; `value` is there for when the detail matters.
 */
export function trackAssist(
  assist: DrawingAssist,
  value: string | number | boolean,
): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_ASSIST_TOGGLED, { assist, value });
}

/**
 * An entity actually committed to the drawing. Deliberately distinct from
 * trackTool: an armed tool can produce fifty shapes or none, and "how many
 * markers were placed" is a different question from "how often was the marker
 * tool picked". Tracked at the point where the create command is pushed to
 * history, never on redo.
 */
export type DrawnShape =
  | "line"
  | "polyline"
  | "rectangle"
  | "circle"
  | "ellipse"
  | "freehand"
  | "text"
  | "photo_marker"
  | "defect"
  | "block"
  | "leader"
  | "parametric_asset";

/** How a text entity got its content. */
export type TextVia = "keyboard" | "shortcut_modal" | "handwriting";

export function trackShape(shape: DrawnShape, details?: EventContext): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_SHAPE_CREATED, { shape, ...details });
}

/** An existing entity was altered (edited, annotated, repositioned…). */
export type ShapeChange =
  | "text_edited"
  | "leader_added"
  | "leader_removed"
  | "dyn_block_param_changed"
  | "block_attribute_changed"
  | "photo_marker_shape_changed"
  | "photo_marker_value_changed"
  | "photo_marker_moved"
  | "defect_moved";

export function trackShapeChange(
  change: ShapeChange,
  details?: EventContext,
): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_SHAPE_MODIFIED, { change, ...details });
}

/**
 * One analytics family per drawer tab of the drawing tool. Every family
 * carries "opened" plus the actions its tab exposes, so the back-office can
 * show one breakdown per tab. Keyed unions: a tab cannot emit another tab's
 * action, and a typo is a compile error.
 */
export interface TabActions {
  settings:
    | "opened"
    | "legend_changed"
    | "scale_changed"
    | "zoom_fit"
    | "lineweight_toggled"
    | "handles_size_changed"
    | "ruler_toggled"
    | "ruler_size_changed"
    | "smoothen_changed"
    | "regenerated"
    // Modale Affichage (poignées, règles, aides, taille, outils affichés) :
    // d'où on l'ouvre, pour savoir à terme lequel des deux accès sert.
    | "display_opened_from_settings"
    | "display_opened_from_file_menu"
    | "display_opened_from_ui_changes"
    // Modale « L'interface évolue » : ouverte d'elle-même, ou rouverte.
    | "ui_changes_shown_auto"
    | "ui_changes_opened_from_settings";
  layers:
    | "opened"
    | "layer_visibility_toggled"
    | "layer_lock_toggled"
    | "non_legend_locked"
    | "all_unlocked";
  paper_space:
    | "opened"
    | "layout_changed"
    | "layout_created"
    | "layout_duplicated"
    | "layout_renamed"
    | "layout_deleted";
  blocks: "opened" | "block_picked";
  assets: "opened" | "asset_inserted";
  backups: "opened" | "backup_created" | "backup_imported";
  images: "opened" | "image_imported" | "pdf_imported";
  activity: "opened";
  observations:
    | "opened"
    | "observation_created"
    | "observation_opened"
    | "view_mode_changed"
    | "type_filter_used"
    | "diagnostic_link_opened";
  analysis:
    | "opened"
    | "analysis_started"
    | "ai_pass_done"
    | "group_validated"
    | "group_ignored"
    | "group_corrected"
    | "suggestion_regenerated";
}

export type DrawerTabKey = keyof TabActions;

const TAB_EVENT_TYPES: Record<DrawerTabKey, EventType> = {
  settings: EventType.DRAWING_TAB_SETTINGS,
  layers: EventType.DRAWING_TAB_LAYERS,
  paper_space: EventType.DRAWING_TAB_PAPER_SPACE,
  blocks: EventType.DRAWING_TAB_BLOCKS,
  assets: EventType.DRAWING_TAB_ASSETS,
  backups: EventType.DRAWING_TAB_BACKUPS,
  images: EventType.DRAWING_TAB_IMAGES,
  activity: EventType.DRAWING_TAB_ACTIVITY,
  observations: EventType.DRAWING_TAB_OBSERVATIONS,
  analysis: EventType.DRAWING_TAB_ANALYSIS,
};

export function trackTab<T extends DrawerTabKey>(
  tab: T,
  action: TabActions[T],
  details?: EventContext,
): void {
  if (isRestoring) return;
  enqueueEvent(TAB_EVENT_TYPES[tab], { action, ...details });
}

/** Actions of the selection bar (SelectionMenu), keyboard shortcuts included. */
export type SelectionAction =
  | "duplicate"
  | "delete"
  | "escape"
  | "finish_polyline"
  | "copy_from_point"
  | "move_from_point"
  | "rotate_from_point"
  | "scale_from_point"
  | "text_edit_opened"
  | "photo_marker_size_changed"
  | "layer_changed"
  | "create_observation"
  | "image_rotated";

export function trackSelection(
  action: SelectionAction,
  details?: EventContext,
): void {
  if (isRestoring) return;
  enqueueEvent(EventType.DRAWING_SELECTION, { action, ...details });
}

/**
 * Whether the restore suppression is currently active. Needed by callers that
 * DEBOUNCE their tracking: the deferred fire runs after the suppression window
 * has closed, so the flag must be checked when scheduling, not when firing —
 * otherwise every drawing open emits phantom "setting changed" events.
 */
export function isRestoringActive(): boolean {
  return isRestoring;
}
